import { ContextWindow } from "@asup/context-menu";
import { isEqual } from "lodash";
import { ReactNode } from "react";
import styles from "../aic.module.css";
import { AitCellAlign } from "./AitCellAlign";
import { AitCellColSpans } from "./AitCellColSpans";
import { AitCellIndents } from "./AitCellIndents";
import { AitCellRowSpans } from "./AitCellRowSpans";
import { AitCellWidth } from "./AitCellWidth";
import { AitCommentRow } from "./AitCommentRow";
import { AitCellData } from "./functions/AitCellData";
import { getBaseId } from "./functions/getBaseId";
import {
  AitCellType,
  AitHeaderCellData,
  AitLocationBodyCell,
  AitLocationHeaderCell,
  AitTablePosition,
  HIDE_OPTIONS_WINDOW,
  UPDATE_HEADER_CELL,
  UPDATE_TABLE_CELL,
} from "./functions/interface";
import { trGetCellFromLocation } from "./functions/trGetCellFromLocation";
import { useTableContext } from "./TableContextProvider";

interface AitCellWindowProps {
  location: AitLocationHeaderCell | AitLocationBodyCell;
}

/**
 * Render the context window for a cell providing editing controls.
 *
 * @template T - Cell payload type
 * @param props - Component props
 * @param props.location - The cell location (header or body)
 */
export const AitCellWindow = <T,>({ location }: AitCellWindowProps): ReactNode => {
  // Context
  const { state, dispatch } = useTableContext<T>();
  const Editor = state?.editorProps.Editor;
  const thisCell: AitHeaderCellData<T> | AitCellData<T> | undefined = state?.processedTableData
    ? trGetCellFromLocation(state.processedTableData, location)
    : undefined;
  const id = getBaseId(location, state?.id);

  return !Editor || !thisCell ? null : (
    <ContextWindow
      id={`${id}-window`}
      key="Cell"
      title={"Cell options"}
      visible={state.showOptionsWindow.findIndex((l) => isEqual(l, location)) > -1}
      onClose={() => {
        dispatch({
          operation: HIDE_OPTIONS_WINDOW,
          location,
        });
      }}
    >
      <AitCommentRow location={location} />
      <div className={styles.aiwBodyRow}>
        <label
          id={`${id}-unprocessed-label`}
          className={styles.aioLabel}
          htmlFor={`${id}-unprocessed`}
        >
          Unprocessed text:
        </label>
        <div className={styles.aioInputHolder}>
          <Editor
            id={`${id}-unprocessed`}
            value={thisCell.text}
            setValue={(ret) =>
              location.position === AitTablePosition.bodyCell
                ? dispatch({
                    operation: UPDATE_TABLE_CELL,
                    location,
                    cellData: { text: ret },
                  })
                : dispatch({
                    operation: UPDATE_HEADER_CELL,
                    location,
                    cellData: { text: ret },
                  })
            }
            editable={!thisCell.readOnly && !location.isRepeat && state.editable}
            className={
              !thisCell.readOnly && !location.isRepeat && state.editable ? "can-edit" : "readonly"
            }
            textAlignment={thisCell.justifyText}
            decimalAlignPercent={state.processedTableData?.decimalAlignPercent}
            aria-labelledby={`${id}-unprocessed-label`}
          />
        </div>
      </div>
      <AitCellAlign location={location} />
      {location.position === AitTablePosition.headerCell && (
        <>
          <AitCellWidth location={location} />
          <AitCellRowSpans location={location} />
          <AitCellColSpans location={location} />
        </>
      )}
      {location.cellType === AitCellType.rowHeader && <AitCellIndents location={location} />}
    </ContextWindow>
  );
};

AitCellWindow.displayName = "AitCellWindow";
